/* eslint-env browser */
/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {FormattedMessage, injectIntl, defineMessages} from 'react-intl';
import moment from 'moment';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';

import Editor from '../fw/Editor.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import FormFields from '../fw/FormFields.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import * as BrowserPermission from '../BrowserPermission';
import {DEFAULT_CURRENCY} from '../../common/locales';
import {RadioGroup} from 'formsy-react-components';
import ApproveOrganisationIdentNumChange from './mutations/ApproveOrganisationIdentNumChange.react';
import RejectOrganisationIdentNumChange from './mutations/RejectOrganisationIdentNumChange.react';
import { computeBilledByChain } from './BilledByChainBreadcrumb.react';

const messages = defineMessages({
    title: {id: 'organisationEdit.title', 'defaultMessage': 'Editing organisation'},
    billType: {id: 'organisationAdd.billType', 'defaultMessage': 'Billing type'},
    separete: {id: 'organisationAdd.separete', 'defaultMessage': 'I want to invoice separately'},
    together: {id: 'organisationAdd.together', 'defaultMessage': 'I want to invoice together'},
    identPendingInfo: { id: 'organisation.ident.pendingInfo', defaultMessage: 'Requested to set Company iD number to {value}.  It is waiting for approval.' },
    identRequestTitle: { id: 'organisation.ident.requestTitle', defaultMessage: 'Request to assign a new Company iD number.' },
    identRequestPlaceholder: { id: 'organisation.ident.requestPlaceholder', defaultMessage: 'New Company iD number' },
    identRequestButton: { id: 'organisation.ident.requestButton', defaultMessage: 'Request to change' },
    identApproveTitle: { id: 'organisation.ident.approveTitle', defaultMessage: 'It is waiting for approval of a new Company iD number' },
    identApprove: { id: 'organisation.ident.approve', defaultMessage: 'Approve' },
    identReject: { id: 'organisation.ident.reject', defaultMessage: 'Reject' },
    identCurrentLabel: { id: 'organisation.ident.current', defaultMessage: 'Company ID number (current)' },
    requestedBy: { id: 'organisation.ident.requestedBy', defaultMessage: 'Requested by:' },
    vatIdWarning: { id: 'organisationEdit.vatId.warning', defaultMessage: 'VAT ID should start with a two-letter country code (e.g. CZ, SK, DE). For Slovakia, use IČ DPH, not DIČ.' },
});

class OrganisationEdit extends React.Component {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };
    typeName = 'organisations';

    constructor(props) {
        super(props);

        let billType = 'separate';
        let data = props.user.organisation;

        if (data.billedBy_id) {
            billType = 'together';
        }

        this.state = {
            billType: billType,
            newIdentNum: '',
            justRequested: false,
            selectedBilledByOrg: null,
        };
    }

    render() {
        let data = this.props.user.organisation;
        let type = types[this.typeName];

        const escapeHtml = (s) => {
            if (s === null || s === undefined) return '';
            return String(s)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

        const billedByDescription = this.props.intl.formatMessage({
            id: 'metadata.organisations.billedBy.description',
            defaultMessage: 'Bill this organisation as part of another organisation.',
        });
        const billedByChainLabel = this.props.intl.formatMessage({
            id: 'organisationEdit.billingChain.chain',
            defaultMessage: 'Chain',
        });

        if (!data['billingCurrency']) {
            data['billingCurrency'] = DEFAULT_CURRENCY;
        }

        // Organisations that can be selected as "billedBy" (consolidated billing target).
        //
        // IMPORTANT: We intentionally do NOT filter out organisations that already have their own billedBy_id.
        // Multi-level consolidated billing (A→B→C) is supported on backend, so the UI must allow choosing B even if B is billed by C.
        let orgsToBill = this.props.user.organisation.relatedOrganisations.edges
            .map(({node}) => node)
            .filter((org) => org.realId !== this.props.user.organisation.realId);

        // Defensive: ensure the currently selected billedBy org is present in the values list,
        // otherwise the Editor select can render as "empty" even though billedBy_id is set.
        if (data.billedBy && data.billedBy.realId) {
            const alreadyIncluded = orgsToBill.find((o) => o && o.realId === data.billedBy.realId);
            if (!alreadyIncluded) {
                orgsToBill = [...orgsToBill, data.billedBy];
            }
        }

        let consultants = this.props.user.organisation.instance.consultantUsers.edges.map(({node}) => node);


        let columns = ['name', 'description', 'email', 'phoneNumber', 'www', 'logo', 'billingName', 'billingEmail', 'billingCurrency', 'identNum', 'vatId', 'street', 'city', 'zip', 'country', 'billedBy', 'useInkaso', 'bankAccount'];
        let mainColumns = {'name': {}, 'description': {}};

		const isDeveloper = BrowserPermission.isDeveloper();
		const isPartner1 = BrowserPermission.isPartner1();
        if (isDeveloper) {
            columns.push("implementationDetails");
            columns.push("implementationDetailsLink");
            columns.push("consultant");
            columns.push("variableSymbol");
            columns.push("versionStrategy");
            columns.push("blocked");
            columns.push("blockedReason");
        }
        if (isPartner1) {
            columns.push("adminExtraEmail");
        }
        if (isDeveloper) {
            mainColumns["implementationDetails"] = {};
            mainColumns['implementationDetailsLink'] = {};
            mainColumns["adminExtraEmail"] = {};
            mainColumns['versionStrategy'] = {};
            mainColumns["consultant"] = {
                values: consultants,
                displayKey: 'name'
            };
            mainColumns['variableSymbol'] = {};
            mainColumns['blocked'] = {};
            mainColumns['blockedReason'] = {};
            mainColumns['blockedAt'] = {};
        }

        let billingRequired = false;
        let showBillingSelection = true;
        if (this.state.billType === 'separate') {
            billingRequired = true;
        }
        if (orgsToBill.length === 0) {
            showBillingSelection = false;
            billingRequired = true;
        }

        let billingColumns = {
            'billingName': {
                required: billingRequired,
            },
            'billingEmail': {
                required: billingRequired,
            },
            'vatId': {
                validate: (values, value) => {
                    if (!value || value.trim() === '') return true;
                    if (!/^[A-Za-z]{2}/.test(value.trim())) {
                        return this.props.intl.formatMessage(messages.vatIdWarning);
                    }
                    return true;
                },
            },
            'street': {},
            'city': {},
            'zip': {},
            'country': {
                required: billingRequired,
            },
            'billingCurrency': {
                required: billingRequired,
            },
            'useInkaso': {},
            'bankAccount': {},
        };
        // identNum se renderuje zvlášť (nad DIČ), ne přes billingColumns

        if (!billingRequired) {
            billingColumns = {
                'billedBy': {
                    values: orgsToBill,
                    displayKey: 'name',
                    onChange: (key, value) => {
                        // value is an organisation primary key (usually realId); map it back to the object for preview rendering
                        const selected = orgsToBill.find((o) => o && (o.realId === value || o.id === value));
                        this.setState({ selectedBilledByOrg: selected || null });
                    },
                },
            };
        } else {
            if (isDeveloper) {
                columns.push("billingFrequency");
                billingColumns['billingFrequency'] = {};
                columns.push("reviewInvoice");
                billingColumns['reviewInvoice'] = {};
                columns.push("customerOrderNo");
                billingColumns['customerOrderNo'] = {};
                columns.push("customerInvoiceText");
                billingColumns['customerInvoiceText'] = {};
                columns.push("nextInvoiceTask");
                billingColumns['nextInvoiceTask'] = {};
            }
        }

        return (
            <AppPanel breadcrumbParams={[
                {type, data}
            ]} title={this.props.intl.formatMessage(messages.title)}>
                <Editor
                        cancelable
                        columns={columns}
                        data={data}
                        disableAutoFields
                        fatQuery={Relay.QL `fragment on UpdateOrganisationPayload {
                            updatedNode {
                                id,
                                realId,
                                name,
                                billingName,
                                billingEmail,
                                billingCurrency,
                                implementationDetails,
                                implementationDetailsLink,
                                adminExtraEmail,
                                description,
                                billingFrequency,
                                reviewInvoice,
                                versionStrategy,
                                isBillable,
                                billingInfoFilled,
                                showMissingBillingInfo,
                                blocked,
                                blockedReason,
                                blockedAt,
                                customerOrderNo,
                                customerInvoiceText,
                                nextInvoiceTask,
                                street,
                                city,
                                zip,
                                useInkaso,
                                variableSymbol,
                                consultant_id,
                                consultant {
                                    id,
                                },
                                bankAccount,
                                email,
                                www,
                                logo,
                                phoneNumber,
                                identNum,
                                vatId,
                                country,
                                billedBy {
                                    id,
                                },
                                billedBy_id,
                            }
                        }`}
                        metadata={type}
                        mutation={Relay.QL `mutation { updateOrganisation }`}
                        onBeforeSubmit={async (data) => {
                            if (this.state.billType === 'separate') {
                                data['billedBy'] = '';
                            }
                            return data;
                        }}
                        onSuccess={() => {
                            if (typeof window !== 'undefined' && window.location) {
                                window.location.href = `/organisations/${data.realId}/`;
                            } else {
                                this.props.relay.forceFetch();
                            }
                        }}
						organisationId={data.realId}
                        urlMapping={urlMapping}
                >
                    <FormFields columns={mainColumns}
                                data={data}
                                metadata={type}
                    />

                    <div className="bs-callout bs-callout-success">
                        <h4><FormattedMessage defaultMessage="Contact information (optional)" id="organisation.contact" /></h4>
                        <FormFields columns={['email', 'phoneNumber', 'www', 'logo']}
                                    data={data}
                                    metadata={type}
                        />
                    </div>

                    <div className="bs-callout bs-callout-default">
                        <h4><FormattedMessage defaultMessage="Billing information (required)" id="organisation.billingRequired" /></h4>

                        {!isDeveloper && data.pendingIdentNum ? (
                            <div className="bs-callout bs-callout-warning" style={{marginTop: 0}}>
                                <h4><FormattedMessage {...messages.identRequestTitle} /></h4>
                                <div style={{marginBottom: 8}}>
                                    <FormattedMessage {...messages.identPendingInfo} values={{ value: data.pendingIdentNum }} />
                                    {data.identNumChangeRequestedBy ? (
                                        <div style={{color:'#777', fontSize: 12, marginTop: 4}}>
                                            <FormattedMessage {...messages.requestedBy} />{' '}
                                            {(data.identNumChangeRequestedBy.name || '')}
                                            {data.identNumChangeRequestedBy.email ? ` (${data.identNumChangeRequestedBy.email})` : ''}
                                            {data.identNumChangeRequestedAt ? `, ${moment(data.identNumChangeRequestedAt).format('DD.MM.YYYY HH:mm')}` : ''}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        {isDeveloper && data.pendingIdentNum ? (
                            <div className="bs-callout bs-callout-warning" style={{marginTop: 0}}>
                                <h4><FormattedMessage {...messages.identApproveTitle} /></h4>
                                <div style={{marginBottom: 8}}>
                                    <FormattedMessage {...messages.identPendingInfo} values={{ value: data.pendingIdentNum }} />
                    {data.identNumChangeRequestedBy ? (
                        <div style={{color:'#777', fontSize: 12, marginTop: 4}}>
                            <FormattedMessage {...messages.requestedBy} />{' '}
                            {(data.identNumChangeRequestedBy.name || '')}
                            {data.identNumChangeRequestedBy.email ? ` (${data.identNumChangeRequestedBy.email})` : ''}
                            {data.identNumChangeRequestedAt ? `, ${moment(data.identNumChangeRequestedAt).format('DD.MM.YYYY HH:mm')}` : ''}
                        </div>
                    ) : null}
                                </div>
                                <div>
                                    <button
                                        className="btn btn-success"
                                        onClick={() => {
                                            Relay.Store.commitUpdate(new ApproveOrganisationIdentNumChange({ organisationId: data.id }), {
                                                onSuccess: () => { if (typeof window !== 'undefined' && window.location) { window.location.reload(); } else { this.props.relay.forceFetch(); } },
                                            });
                                        }}
                                        style={{marginRight: 8}}
                                        type="button"
                                    >
                                        <FormattedMessage {...messages.identApprove} />
                                    </button>
                                    <button
                                        className="btn btn-default"
                                        onClick={() => {
                                            Relay.Store.commitUpdate(new RejectOrganisationIdentNumChange({ organisationId: data.id }), {
                                                onSuccess: () => this.props.relay.forceFetch(),
                                            });
                                        }}
                                        type="button"
                                    >
                                        <FormattedMessage {...messages.identReject} />
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        {showBillingSelection ? <div className="formGroup">
                            <RadioGroup
                                label={this.props.intl.formatMessage(messages.billType)}
                                name="billedSeparatedly"
                                onChange={(key, val) => {
                                    this.setState({billType: val});
                                }}
                                options={[
                                    {value: 'separate', label: this.props.intl.formatMessage(messages.separete)},
                                    {value: 'together', label: this.props.intl.formatMessage(messages.together)},
                                ]}
                                value={this.state.billType}
                            />
                        </div> : null}

                        {billingRequired ? (() => {
                            const billingColumnsTop = {
                                billingName: billingColumns['billingName'],
                                billingEmail: billingColumns['billingEmail'],
                            };
                            const billingColumnsRest = { ...billingColumns };
                            delete billingColumnsRest['billingName'];
                            delete billingColumnsRest['billingEmail'];
                            delete billingColumnsRest['identNum'];

                            return (
                                <div>
                                    <FormFields columns={billingColumnsTop}
                                                data={data}
                                                metadata={type}
                                    />

                                    {(data.pendingIdentNum || data.identNumChangeRequestedAt) ? (
                                        <FormFields columns={{ identNum: { readOnly: true, name: this.props.intl.formatMessage(messages.identCurrentLabel) } }}
                                                    data={data}
                                                    metadata={type}
                                        />
                                    ) : (
                                        <FormFields columns={{ identNum: { required: true } }}
                                                    data={data}
                                                    metadata={type}
                                        />
                                    )}

                                    <FormFields columns={billingColumnsRest}
                                                data={data}
                                                metadata={type}
                                    />
                                </div>
                            );
                        })() : (
                            <div>
                                <FormFields columns={billingColumns}
                                        data={data}
                                        metadata={type}
                                        onField={(column, addons) => {
                                            if (column !== 'billedBy') return addons;

                                            const billedByOrg = this.state.selectedBilledByOrg || data.billedBy;
                                            const { chain, finalOrg } = computeBilledByChain({ sourceOrg: data, billedByOrg, maxDepth: 10 });
                                            if (!billedByOrg || !finalOrg || finalOrg === data || !chain || chain.length < 2) {
                                                return addons;
                                            }

                                            const orgLinkHtml = (org) => {
                                                const id = (org && (org.realId || org.id)) ? (org.realId || org.id) : '';
                                                const name = escapeHtml(org && org.name ? org.name : '?');
                                                if (!id) return name;
                                                return `<a href="/organisations/${escapeHtml(id)}/edit/">${name}</a>`;
                                            };

                                            const chainHtml = chain.map(orgLinkHtml).join(' &#8594; ');
                                            const desc = escapeHtml(String(billedByDescription || '').trim()).replace(/\.*\s*$/, '');
                                            const chainLabel = escapeHtml(String(billedByChainLabel || '').trim()).replace(/\.*\s*$/, '');
                                            const blockHtml =
                                                `<div style="margin-top:4px">` +
                                                    `<div><strong>${chainLabel}:</strong> ${chainHtml}</div>` +
                                                    `<div>${desc}: ${orgLinkHtml(finalOrg)}</div>` +
                                                `</div>`;

                                            // IMPORTANT:
                                            // SearchableSelect renders `help` separately via Row.renderHelp().
                                            // If we also provide htmlHelp, the description would appear twice.
                                            addons.help = null;
                                            addons.htmlHelp = (addons.htmlHelp || '') + blockHtml;
                                            return addons;
                                        }}
                                />
                            </div>
                        )}



                        <p><FormattedMessage defaultMessage="You will be billed by invoice." id="organisation.billingNote" /></p>
                    </div>

                </Editor>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(OrganisationEdit), (props) => {
    return props.user.organisation;
}), {
    initialVariables: {
        organisationId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    billingName,
                    billingEmail,
                    billingCurrency,
                    blocked,
                    blockedReason,
                    blockedAt,
                    name,
                    description,
                    street,
                    city,
                    zip,
                    email,
                    phoneNumber,
                    consultant_id,
                    consultant {
                        id,
                        realId,
                    },
                    implementationDetails,
                    implementationDetailsLink,
                    adminExtraEmail,
                    billingFrequency,
                    reviewInvoice,
                    versionStrategy,
                    customerOrderNo,
                    customerInvoiceText,
                    nextInvoiceTask,
                    www,
                    logo,
                    vatId,
                    country,
                    identNum,
                    pendingIdentNum,
                    identNumChangeRequestedAt,
                    identNumChangeRequestedBy { id, realId, name, email },
                    useInkaso,
                    variableSymbol,
                    bankAccount,
                    billedBy_id,
                    billedBy {
                        id,
                        realId,
                        name,
                        billedBy {
                            id,
                            realId,
                            name,
                            billedBy {
                                id,
                                realId,
                                name,
                                billedBy {
                                    id,
                                    realId,
                                    name,
                                    billedBy {
                                        id,
                                        realId,
                                        name,
                                        billedBy {
                                            id,
                                            realId,
                                            name,
                                        },
                                    },
                                },
                            },
                        },
                    },
                    relatedOrganisations(first: 500) {
                        edges {
                            node {
                                id,
                                realId,
                                name,
                                billedBy_id,
                                billedBy {
                                    id,
                                    realId,
                                    name,
                                    billedBy {
                                        id,
                                        realId,
                                        name,
                                        billedBy {
                                            id,
                                            realId,
                                            name,
                                            billedBy {
                                                id,
                                                realId,
                                                name,
                                                billedBy {
                                                    id,
                                                    realId,
                                                    name,
                                                    billedBy {
                                                        id,
                                                        realId,
                                                        name,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            }
                        }
                    },
                    instance {
                        consultantUsers(first: 1000) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    name,
                                    email,
                                    picture
                                }
                            }
                        }
                    }    
                }
            }
        `,
    },
});
